import React from 'react';
import { Box, Typography, TextField, Button, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import StatusPill from '../ui/StatusPill';
import EmptyState from '../ui/EmptyState';
import { ArrowUp, ArrowDown } from '../ui/Icons';

// Pencil (rename) icon — mirrors the local PencilIcon convention used in
// MenuItemActionsMenu.jsx (we don't ship @mui/icons-material).
const PencilIcon = (props) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: props.size || 18, height: props.size || 18 }}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </Box>
);

const TrashIcon = (props) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: props.size || 18, height: props.size || 18 }}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </Box>
);

// Globe (translate) icon — same local-SVG convention as Pencil/Trash above.
const GlobeIcon = (props) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: props.size || 18, height: props.size || 18 }}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
  </Box>
);

/**
 * Categories management for Menu Management v2. A "category" here is the menu
 * item `section` field — the ordered source of truth is the restaurant's
 * `section_order`. Presentational: all data + handlers come in via props,
 * matching the CategoryPills / MenuItemActionsMenu sibling pattern.
 */
const CategoriesTab = ({
  sectionOrder,
  menuItems,
  newCategoryName,
  onNewCategoryNameChange,
  onAddCategory,
  onMoveUp,
  onMoveDown,
  onRenameClick,
  onDeleteClick,
  onTranslateClick,
  sectionTranslations = {},
  getLabel = (s) => s,
  busy = false,
}) => {
  const { t } = useTranslation();

  const countFor = (name) =>
    menuItems.filter((i) => (i.section || 'Other') === name).length;

  // Languages with a saved translation for this category (for the row indicator).
  const translatedLangsFor = (name) => {
    const tr = sectionTranslations[name];
    if (!tr) return [];
    return ['es', 'ar'].filter((lang) => tr[lang] && tr[lang].name);
  };

  return (
    <Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 0.5 }}>
          {t('menuV2.categories.title', 'Categories')}
        </Typography>
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
          {t('menuV2.categories.description', 'Add categories, rename them, or reorder. Deleting a category moves its items to "Other".')}
        </Typography>
      </Box>

      {/* Inline add row — the PageHeader "+ Add Item" button only renders on the
          items tab, so the add affordance lives here. */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, maxWidth: 480 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t('menuV2.categories.addPlaceholder', 'New category name...')}
          value={newCategoryName}
          onChange={(e) => onNewCategoryNameChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAddCategory();
            }
          }}
        />
        <Button variant="contained" onClick={onAddCategory} disabled={busy} sx={{ flexShrink: 0 }}>
          {t('menuV2.categories.add', 'Add')}
        </Button>
      </Box>

      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {sectionOrder.length === 0 ? (
          <EmptyState
            icon="📂"
            title={t('menuV2.categories.emptyTitle', 'No categories yet')}
            description={t('menuV2.categories.emptyDescription', 'Add a category above, or assign one when editing a menu item.')}
          />
        ) : (
          sectionOrder.map((name, index) => (
            <Box
              key={name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderBottom: index === sectionOrder.length - 1 ? 'none' : '1px solid',
                borderColor: 'divider',
                transition: 'background-color 120ms ease',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                {getLabel(name)}
              </Typography>
              {translatedLangsFor(name).length > 0 && (
                <StatusPill
                  status="success"
                  label={translatedLangsFor(name).map((l) => l.toUpperCase()).join(' · ')}
                />
              )}
              <StatusPill
                status="info"
                label={t('menuV2.categories.itemCount', { count: countFor(name) })}
              />
              <Box sx={{ display: 'inline-flex', gap: 0.5, flexShrink: 0 }}>
                <IconButton
                  size="small"
                  onClick={() => onMoveUp(index)}
                  disabled={index === 0 || busy}
                  aria-label={t('menuV2.categories.moveUp', 'Move up')}
                >
                  <ArrowUp size={18} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onMoveDown(index)}
                  disabled={index === sectionOrder.length - 1 || busy}
                  aria-label={t('menuV2.categories.moveDown', 'Move down')}
                >
                  <ArrowDown size={18} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onTranslateClick(name)}
                  disabled={busy}
                  aria-label={t('menuV2.categories.translate', 'Translate')}
                >
                  <GlobeIcon size={18} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onRenameClick(name)}
                  disabled={busy}
                  aria-label={t('common.edit', 'Edit')}
                >
                  <PencilIcon size={18} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDeleteClick(name)}
                  disabled={busy}
                  aria-label={t('common.delete', 'Delete')}
                  sx={{ color: 'error.main' }}
                >
                  <TrashIcon size={18} />
                </IconButton>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default CategoriesTab;
